import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { QBox, QStack } from '@captivateiq/quotient';

import { useAIShellContext } from '@captivateiq/assistant/AIShellContext';
import type { AttachmentMeta, Message } from '@captivateiq/assistant/types';
import { DebugSteps } from './DebugSteps';
import { ThinkingComplete } from './ThinkingComplete';
import { ThinkingLoader } from './ThinkingLoader';
import { AgentMessage } from './AgentMessage';
import { ErrorMessage } from './ErrorMessage';
import { MessageButtonBar } from './MessageButtonBar';
import { MultipleChoiceOptions } from './MultipleChoiceOptions';
import { PendingApprovalCard } from './PendingApprovalCard';
import { UserMessage } from './UserMessage';

import { TabLayout } from '../../TabLayout';
import { expandedTextColumnSx } from '../../shared/expandedTextColumnSx';
import { AgentIntroduction } from '../shared/AgentIntroduction';
import { ExperimentalDisclaimer } from '../shared/ExperimentalDisclaimer';
import { MessageInput } from '../shared/MessageInput';

// How long a message stays the reveal target after arriving — comfortably longer than the reveal
// animation itself ever takes, so a remount within that window (e.g. a parent re-key) still gets
// to play the reveal, but the id doesn't linger indefinitely afterward and replay on some later,
// unrelated remount of what's by then a historical message.
const REVEAL_TARGET_TTL_MS = 5000;

export const ChatView = () => {
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const thinkingStartTimeRef = useRef<number | null>(null);
  const [lastThinkingDuration, setLastThinkingDuration] = useState<number | null>(null);
  const wasThinkingRef = useRef(false);
  // Only the agent message that just finished generating in this session gets the typewriter
  // reveal — history reopened from a past conversation should render instantly, not replay it.
  const [newlyArrivedMessageId, setNewlyArrivedMessageId] = useState<string | null>(null);
  const revealTargetClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    selectedAgent,
    currentConversation,
    send,
    agentThinking,
    messageSendError,
    getMessageButtons,
    getMessageAppActions,
    getPanelButtons,
    choices,
    pendingApproval,
    features,
    debugMode,
    isExpanded,
    feedbackEnabled,
    messageFeedback,
    messageFeedbackResponse,
    handleFeedback,
    handleFeedbackResponse,
  } = useAIShellContext();

  // Track thinking duration
  useEffect(() => {
    if (agentThinking) {
      thinkingStartTimeRef.current = Date.now();
      setLastThinkingDuration(null);
    } else if (thinkingStartTimeRef.current !== null) {
      const elapsed = Math.round((Date.now() - thinkingStartTimeRef.current) / 1000);
      setLastThinkingDuration(elapsed);
      thinkingStartTimeRef.current = null;
    }
  }, [agentThinking]);

  const messages = useMemo(() => currentConversation?.messages || [], [currentConversation?.messages]);

  // Fires exactly once per response: when agentThinking flips true -> false, the agent message
  // that was just appended is the one that should type itself out. Anything already in the
  // conversation when it's opened never sees this transition, so history renders instantly.
  useEffect(() => {
    if (wasThinkingRef.current && !agentThinking) {
      const lastAgentMessage = [...messages].reverse().find((m) => m.type === 'agent');
      if (lastAgentMessage) {
        setNewlyArrivedMessageId(lastAgentMessage.id);
        if (revealTargetClearTimeoutRef.current !== null) clearTimeout(revealTargetClearTimeoutRef.current);
        // Also clear after a generous TTL even if nothing else happens — otherwise a remount of
        // this same message within this conversation (e.g. a parent re-key) would replay the
        // reveal indefinitely into the future, long after it's become a historical message.
        revealTargetClearTimeoutRef.current = setTimeout(() => {
          setNewlyArrivedMessageId(null);
        }, REVEAL_TARGET_TTL_MS);
      }
    } else if (agentThinking) {
      // Clear the previous reveal target as soon as a new request starts, rather than leaving it
      // set indefinitely — otherwise revisiting that same message later (e.g. after switching
      // conversations and back) would replay the reveal on what is now a historical message.
      if (revealTargetClearTimeoutRef.current !== null) clearTimeout(revealTargetClearTimeoutRef.current);
      setNewlyArrivedMessageId(null);
    }
    wasThinkingRef.current = agentThinking;
  }, [agentThinking, messages]);

  // Belt-and-suspenders: also clear on conversation switch, in case one conversation's agent
  // response finishes while a different conversation is (or becomes) the active one.
  useEffect(() => {
    if (revealTargetClearTimeoutRef.current !== null) clearTimeout(revealTargetClearTimeoutRef.current);
    setNewlyArrivedMessageId(null);
  }, [currentConversation?.id]);

  // Clean up a pending clear timeout if ChatView itself unmounts first.
  useEffect(() => {
    return () => {
      if (revealTargetClearTimeoutRef.current !== null) clearTimeout(revealTargetClearTimeoutRef.current);
    };
  }, []);

  const sendAndScroll = useCallback(
    async (message: string, attachments?: AttachmentMeta[]) => {
      if (agentThinking) {
        return;
      }
      bottomSentinelRef.current?.scrollIntoView({ behavior: 'auto' });
      await send(message, attachments?.length ? { attachments } : undefined);
    },
    [send, agentThinking],
  );

  useEffect(() => {
    bottomSentinelRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages]);

  return (
    <TabLayout
      bottomContent={
        <QStack spacing="8px">
          <MessageInput agentThinking={agentThinking} onSubmit={sendAndScroll} />
          <QBox sx={expandedTextColumnSx(isExpanded)}>
            <ExperimentalDisclaimer />
          </QBox>
        </QStack>
      }
    >
      <QBox sx={expandedTextColumnSx(isExpanded)}>
        <AgentIntroduction agentName={selectedAgent ?? ''} />
      </QBox>
      <QStack
        data-testid="ai-chat-view"
        direction="column"
        spacing="10px"
        sx={{
          alignItems: 'stretch',
          backgroundColor: 'surface.background.mediumContrast',
          flex: 1,
          padding: '16px',
        }}
      >
        <QBox sx={expandedTextColumnSx(isExpanded)}>
          <MessageButtonBar align="right" buttons={getPanelButtons()} />
        </QBox>
        {messages.map((message: Message, index: number) => {
          const buttons = getMessageButtons(message.id);
          const isLastAgentMessage =
            message.type === 'agent' && !messages.slice(index + 1).some((m) => m.type === 'agent');

          switch (message.type) {
            case 'user':
              return <UserMessage buttons={buttons} key={message.id} message={message} />;
            case 'agent':
              return (
                <QStack direction="column" key={message.id} spacing="10px">
                  {debugMode && message.debug_messages && message.debug_messages.length > 0 && (
                    <QBox sx={expandedTextColumnSx(isExpanded)}>
                      <DebugSteps steps={message.debug_messages} />
                    </QBox>
                  )}
                  {isLastAgentMessage && !agentThinking && lastThinkingDuration !== null && (
                    <QBox sx={expandedTextColumnSx(isExpanded)}>
                      <ThinkingComplete elapsedSeconds={lastThinkingDuration} />
                    </QBox>
                  )}
                  <AgentMessage
                    animateReveal={message.id === newlyArrivedMessageId}
                    appActions={getMessageAppActions(message.id)}
                    buttons={buttons}
                    feedback={feedbackEnabled ? messageFeedback.get(message.id) ?? null : undefined}
                    feedbackResponse={feedbackEnabled ? messageFeedbackResponse.get(message.id) ?? null : undefined}
                    message={message}
                    onFeedback={feedbackEnabled ? handleFeedback : undefined}
                    onFeedbackResponse={feedbackEnabled ? handleFeedbackResponse : undefined}
                  />
                  {isLastAgentMessage && !agentThinking && features.multipleChoice && choices.length > 0 && (
                    <QBox sx={expandedTextColumnSx(isExpanded)}>
                      <MultipleChoiceOptions choices={choices} />
                    </QBox>
                  )}
                  {/* No feature toggle: a pending_approval in the response IS the gate, and a
                      queued write must never be silently unapprovable in a host app. */}
                  {isLastAgentMessage && !agentThinking && pendingApproval && (
                    <QBox sx={expandedTextColumnSx(isExpanded)}>
                      <PendingApprovalCard approval={pendingApproval} />
                    </QBox>
                  )}
                </QStack>
              );
            default:
              return null;
          }
        })}
        {agentThinking && (
          <QBox sx={expandedTextColumnSx(isExpanded)}>
            <ThinkingLoader agentName={selectedAgent ?? ''} />
          </QBox>
        )}
        {messageSendError && (
          <QBox sx={expandedTextColumnSx(isExpanded)}>
            <ErrorMessage message={messageSendError} />
          </QBox>
        )}
        <div ref={bottomSentinelRef} />
      </QStack>
    </TabLayout>
  );
};
